import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AssessmentIcon from "@mui/icons-material/Assessment";

// Style constants for cleaner code
const CELL_STYLES = {
  headerBorder: (theme) => `2px solid ${theme.palette.divider}`,
  subHeaderBorder: (theme) => `1px solid ${theme.palette.divider}`,
  sectionColors: {
    trades: { bg: 'transparent', text: 'inherit' },
    shares: { bg: '#343434', text: 'inherit' }, // Light grey for subtle separation
    dollars: { bg: 'transparent', text: 'inherit' },
  },
  widths: {
    orderType: 120,
    data: 80,
    dollars: 100,
  },
};

// Helper functions for cleaner table rendering (defined inside component to access theme)
const createSectionHeaderCell = (content, section, theme) => (
  <TableCell
    align="center"
    colSpan={3}
    sx={{
      fontWeight: 700,
      borderBottom: CELL_STYLES.headerBorder(theme),
      py: 1,
      backgroundColor: CELL_STYLES.sectionColors[section].bg,
      color: CELL_STYLES.sectionColors[section].text,
    }}
  >
    {content}
  </TableCell>
);

const createSubHeaderCell = (content, section, theme) => (
  <TableCell
    align="center"
    sx={{
      fontWeight: 600,
      borderBottom: CELL_STYLES.subHeaderBorder(theme),
      py: 1,
      borderTop: 'none',
      backgroundColor: CELL_STYLES.sectionColors[section].bg,
      color: CELL_STYLES.sectionColors[section].text,
      width: section === 'dollars' ? CELL_STYLES.widths.dollars : CELL_STYLES.widths.data,
    }}
  >
    {content}
  </TableCell>
);

const createDataCell = (content, section, theme, isBold = false) => (
  <TableCell
    align="center"
    sx={{
      py: 1,
      backgroundColor: CELL_STYLES.sectionColors[section].bg,
      color: CELL_STYLES.sectionColors[section].text,
      width: section === 'dollars' ? CELL_STYLES.widths.dollars : CELL_STYLES.widths.data,
      ...(isBold && { fontWeight: 600 }),
    }}
  >
    {content}
  </TableCell>
);

const SummaryDialog = ({ open, onClose, tradesData }) => {
  const theme = useTheme();

  // Calculate summary data
  const summaryData = useMemo(() => {
    if (!tradesData || tradesData.length === 0) {
      return {
        totalStrategies: 0,
        filledAmountLong: 0,
        filledAmountShort: 0,
        totalFilledAmount: 0,
        ordersByType: [],
      };
    }

    // Get unique strategies
    const uniqueStrategies = new Set();
    let filledAmountLong = 0;
    let filledAmountShort = 0;
    let totalFilledAmount = 0;

    // Track orders by type
    const ordersByTypeMap = new Map();

    tradesData.forEach((trade) => {
      // Count unique strategies
      if (trade.strategy && trade.strategy !== "N/A") {
        uniqueStrategies.add(trade.strategy);
      }

      // Calculate filled amounts
      const filledAmount = trade.filledShares * trade.pricePerShare;
      totalFilledAmount += filledAmount;

      // Determine order type based on side (which includes action + position)
      let orderType = "";
      const side = trade.side?.toLowerCase();

      if (side?.includes("buy long")) {
        orderType = "BuyLong";
        filledAmountLong += filledAmount;
      } else if (side?.includes("sell long")) {
        orderType = "SellLong";
        // SellLong is closing long position, so it's not "long position" filled amount
      } else if (side?.includes("cover short")) {
        orderType = "CoverShort";
        filledAmountShort += filledAmount;
      } else if (side?.includes("sell short")) {
        orderType = "SellShort";
        // SellShort is opening short position, so it's not "short position" filled amount
      }

      // Determine if this is a crossing trade (custodian == "Cross Custodian" or similar)
      const isCrossing = trade.custodian?.toLowerCase().includes("cross") || trade.custodian === "CROSS";

      // Track orders by type
      if (!ordersByTypeMap.has(orderType)) {
        ordersByTypeMap.set(orderType, {
          orderType,
          // No of Trades
          noOfTradesMarket: 0,
          noOfTradesCrossing: 0,
          noOfTradesTotal: 0,
          // Shares
          sharesMarket: 0,
          sharesCrossing: 0,
          sharesTotal: 0,
          // Dollars (Filled Amount)
          dollarsMarket: 0,
          dollarsCrossing: 0,
          dollarsTotal: 0,
        });
      }

      const typeData = ordersByTypeMap.get(orderType);

      if (isCrossing) {
        typeData.noOfTradesCrossing += 1;
        typeData.sharesCrossing += trade.quantity;
        typeData.dollarsCrossing += filledAmount;
      } else {
        typeData.noOfTradesMarket += 1;
        typeData.sharesMarket += trade.quantity;
        typeData.dollarsMarket += filledAmount;
      }

      // Total counts
      typeData.noOfTradesTotal += 1;
      typeData.sharesTotal += trade.quantity;
      typeData.dollarsTotal += filledAmount;
    });

    return {
      totalStrategies: uniqueStrategies.size,
      filledAmountLong,
      filledAmountShort,
      totalFilledAmount,
      ordersByType: Array.from(ordersByTypeMap.values()),
    };
  }, [tradesData]);

  // Format currency values
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[8],
        }
      }}
    >
      <DialogTitle sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        pb: 2,
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: "18px",
        fontWeight: 600,
      }}>
        <AssessmentIcon sx={{ color: "primary.main" }} />
        Trades Summary
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* ORDERS SUMMARY Section */}
        <Box sx={{ mb: 1 }}>
         

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 3 }}>
            <Box sx={{
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              backgroundColor: theme.palette.background.paper,
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Total No Of Strategies
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: '1.6rem' }}>
                {summaryData.totalStrategies}
              </Typography>
            </Box>

            <Box sx={{
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              backgroundColor: theme.palette.background.paper,
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Filled Amount in Long Position
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.success.main, fontSize: '1.6rem' }}>
                {formatCurrency(summaryData.filledAmountLong)}
              </Typography>
            </Box>

            <Box sx={{
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              backgroundColor: theme.palette.background.paper,
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Filled Amount in Short Position
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.warning.main, fontSize: '1.6rem' }}>
                {formatCurrency(summaryData.filledAmountShort)}
              </Typography>
            </Box>

            <Box sx={{
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              backgroundColor: theme.palette.background.paper,
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Total Amount
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.info.main, fontSize: '1.6rem' }}>
                {formatCurrency(summaryData.totalFilledAmount)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ORDERS BY TYPE Section */}
        <Box>
        
         

          <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                {/* Top header row with column spans */}
                <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                  <TableCell sx={{ fontWeight: 700, borderBottom: CELL_STYLES.headerBorder(theme), py: 1, width: CELL_STYLES.widths.orderType }}>
                    Order Type
                  </TableCell>
                  {createSectionHeaderCell('No Of Trades', 'trades', theme)}
                  {createSectionHeaderCell('Shares', 'shares', theme)}
                  {createSectionHeaderCell('Filled', 'dollars', theme)}
                </TableRow>
                {/* Second header row with Market/Crossing/Total */}
                <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                  <TableCell sx={{ fontWeight: 600, borderBottom: CELL_STYLES.subHeaderBorder(theme), py: 1, borderTop: 'none' }}>
                    {/* Empty cell for Order Type */}
                  </TableCell>
                  {/* No Of Trades section */}
                  {createSubHeaderCell('Market', 'trades', theme)}
                  {createSubHeaderCell('Crossing', 'trades', theme)}
                  {createSubHeaderCell('Total', 'trades', theme)}
                  {/* Shares section */}
                  {createSubHeaderCell('Market', 'shares', theme)}
                  {createSubHeaderCell('Crossing', 'shares', theme)}
                  {createSubHeaderCell('Total', 'shares', theme)}
                  {/* Dollars section */}
                  {createSubHeaderCell('Market', 'dollars', theme)}
                  {createSubHeaderCell('Crossing', 'dollars', theme)}
                  {createSubHeaderCell('Total', 'dollars', theme)}
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryData.ordersByType.length > 0 ? (
                  summaryData.ordersByType.map((row) => (
                    <TableRow key={row.orderType} hover>
                      <TableCell sx={{ fontWeight: 500, py: 1, width: CELL_STYLES.widths.orderType }}>
                        {row.orderType}
                      </TableCell>
                      {/* No of Trades */}
                      {createDataCell(row.noOfTradesMarket, 'trades', theme)}
                      {createDataCell(row.noOfTradesCrossing, 'trades', theme)}
                      {createDataCell(row.noOfTradesTotal, 'trades', theme, true)}
                      {/* Shares */}
                      {createDataCell(row.sharesMarket.toLocaleString(), 'shares', theme)}
                      {createDataCell(row.sharesCrossing.toLocaleString(), 'shares', theme)}
                      {createDataCell(row.sharesTotal.toLocaleString(), 'shares', theme, true)}
                      {/* Dollars */}
                      {createDataCell(formatCurrency(row.dollarsMarket), 'dollars', theme)}
                      {createDataCell(formatCurrency(row.dollarsCrossing), 'dollars', theme)}
                      {createDataCell(formatCurrency(row.dollarsTotal), 'dollars', theme, true)}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        No trade data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: 3,
        pb: 1,
        borderTop: `1px solid ${theme.palette.divider}`,
        pt: 1
      }}>
        {/* <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 3,
          }}
        >
          Close
        </Button> */}
      </DialogActions>
    </Dialog>
  );
};

export default SummaryDialog;

